import React, { useState, useEffect, useRef } from 'react';
import { Table, Button, Select, Switch, Drawer, Typography, Space, Spin, Tag, message, notification, Badge } from 'antd';
import { ReloadOutlined, MailOutlined, BellOutlined } from '@ant-design/icons';
import { embotAPI } from '../../services/api';

const { Text } = Typography;

const ACCOUNTS = [
  'jeff@comboware.tw',
  'admin@comboware.tw',
  'jolie@comboware.tw',
  'mira@comboware.tw',
];

const POLL_INTERVAL_MS = 2 * 60 * 1000; // 2 minutes

export default function EMInbox() {
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(false);
  const [account, setAccount] = useState('jeff@comboware.tw');
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [total, setTotal] = useState(0);
  const [unreadCount, setUnreadCount] = useState(0);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [emailLoading, setEmailLoading] = useState(false);
  const [unreadSummary, setUnreadSummary] = useState({});
  const prevUnreadRef = useRef({});

  const fetchInbox = async () => {
    setLoading(true);
    try {
      const res = await embotAPI.inbox({ account, limit: 50, unread_only: unreadOnly });
      const d = res.data;
      if (d.error) {
        message.error(d.error);
        setEmails([]);
      } else {
        setEmails(d.messages || []);
        setTotal(d.total || 0);
        setUnreadCount(d.unread || 0);
      }
    } catch (e) {
      message.error(e.message);
    }
    setLoading(false);
  };

  const pollUnread = async (showNotif = true) => {
    try {
      const res = await embotAPI.unreadSummary();
      const summary = res.data?.accounts || {};
      setUnreadSummary(summary);

      if (showNotif) {
        for (const acc of ACCOUNTS) {
          const prev = prevUnreadRef.current[acc] ?? null;
          const curr = summary[acc] ?? 0;
          if (prev !== null && curr > prev) {
            const newCount = curr - prev;
            notification.info({
              message: `📬 新郵件通知`,
              description: `${acc} 有 ${newCount} 封新未讀郵件`,
              placement: 'topRight',
              duration: 6,
              icon: <MailOutlined style={{ color: '#1890ff' }} />,
            });
          }
        }
      }
      prevUnreadRef.current = summary;
    } catch (_) {}
  };

  useEffect(() => {
    pollUnread(false);
    const timer = setInterval(() => pollUnread(true), POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, []);

  const readEmail = async (uid) => {
    setEmailLoading(true);
    try {
      const res = await embotAPI.readEmail({ account, uid });
      if (res.data.error) {
        message.error(res.data.error);
      } else {
        setSelectedEmail(res.data);
      }
    } catch (e) {
      message.error(e.message);
    }
    setEmailLoading(false);
  };

  const columns = [
    {
      title: '', width: 30,
      render: (_, r) => r.unread ? <Tag color="blue" style={{ margin: 0, padding: '0 4px', fontSize: 10 }}>NEW</Tag> : null,
    },
    { title: 'From', dataIndex: 'from', width: 220, ellipsis: true, render: v => <Text strong>{v}</Text> },
    { title: 'Subject', dataIndex: 'subject', ellipsis: true },
    { title: 'Date', dataIndex: 'date', width: 200, ellipsis: true },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
        <Space>
          <Select value={account} onChange={setAccount} style={{ width: 220 }}>
            {ACCOUNTS.map(acc => (
              <Select.Option key={acc} value={acc}>
                <Badge
                  count={unreadSummary[acc] > 0 ? unreadSummary[acc] : 0}
                  size="small"
                  offset={[6, 0]}
                  color={unreadSummary[acc] === -1 ? 'red' : undefined}
                  title={unreadSummary[acc] === -1 ? '認證失敗' : undefined}
                >
                  <span style={{ paddingRight: 8, color: unreadSummary[acc] === -1 ? '#ff4d4f' : undefined }}>{acc}</span>
                </Badge>
              </Select.Option>
            ))}
          </Select>
          <Switch checked={unreadOnly} onChange={setUnreadOnly} checkedChildren="Unread" unCheckedChildren="All" />
          <Text type="secondary">Total: {total} | Unread: {unreadCount}</Text>
        </Space>
        <Space>
          <Button icon={<BellOutlined />} onClick={() => pollUnread(false)} title="重整未讀統計">
            {Object.values(unreadSummary).reduce((a, b) => a + b, 0) > 0
              ? `全部未讀 ${Object.values(unreadSummary).reduce((a, b) => a + b, 0)}`
              : '無未讀'}
          </Button>
          <Button type="primary" icon={<ReloadOutlined />} onClick={fetchInbox} loading={loading}>
            Fetch Inbox
          </Button>
        </Space>
      </div>

      <Table
        columns={columns}
        dataSource={emails}
        rowKey="uid"
        loading={loading}
        size="middle"
        pagination={{ pageSize: 20 }}
        onRow={(record) => ({
          onClick: () => readEmail(record.uid),
          style: { cursor: 'pointer', fontWeight: record.unread ? 600 : 400 },
        })}
      />

      <Drawer
        title={selectedEmail?.subject || 'Email'}
        open={!!selectedEmail}
        onClose={() => setSelectedEmail(null)}
        width={640}
      >
        {emailLoading ? <Spin /> : selectedEmail && (
          <div>
            <div style={{ marginBottom: 16, padding: 12, background: '#fafafa', borderRadius: 8 }}>
              <div><Text strong>From:</Text> {selectedEmail.from}</div>
              <div><Text strong>To:</Text> {selectedEmail.to}</div>
              <div><Text strong>Date:</Text> {selectedEmail.date}</div>
              {selectedEmail.attachments?.length > 0 && (
                <div><Text strong>Attachments:</Text> {selectedEmail.attachments.join(', ')}</div>
              )}
            </div>
            {selectedEmail.body_html ? (
              <div
                style={{ border: '1px solid #f0f0f0', borderRadius: 8, padding: 16, background: '#fff' }}
                dangerouslySetInnerHTML={{ __html: selectedEmail.body_html }}
              />
            ) : (
              <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', lineHeight: 1.6 }}>
                {selectedEmail.body_text}
              </pre>
            )}
          </div>
        )}
      </Drawer>
    </div>
  );
}
